// Apply for Leave modal — WP plugin parity (Screen E4).
// 4 fields: Leave Type, From Date, To Date, Reason. Server validates
// advance-notice + balance + permissions.

import React, { useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import Field from "../../Components/ui/Field";

const LEAVE_TYPES = [
  { value: "annual",      label: "Annual Leave" },
  { value: "sick",        label: "Sick Leave" },
  { value: "casual",      label: "Casual Leave" },
  { value: "marriage",    label: "Marriage Leave" },
  { value: "bereavement", label: "Bereavement Leave" },
  { value: "examination", label: "Examination Leave" },
  { value: "paternity",   label: "Paternity Leave" },
  { value: "maternity",   label: "Maternity Leave" },
  { value: "half_day",    label: "Half Day" },
  { value: "unpaid",      label: "Unpaid Leave" },
];

// Today in local-timezone YYYY-MM-DD — used as `min=` on date inputs so the
// browser's date picker greys out past dates for the user.
function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function ApplyLeaveModal({ open, onClose, onSubmitted }) {
  const toast = useToast();
  const [type, setType] = useState("annual");
  const [from, setFrom] = useState("");
  const [to, setTo]     = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const today = todayIso();
  const isAdmin = localStorage.getItem("role") === "SUPER_ADMIN";

  if (!open) return null;

  async function submit() {
    if (!from || !to) { toast({ title: "From and To dates are required.", status: "warning" }); return; }
    if (new Date(from) > new Date(to)) { toast({ title: "From date cannot be after To date.", status: "warning" }); return; }
    // Block back-dated requests for non-admins. Admin can still backdate (e.g.
    // recording leave taken before this system was rolled out).
    if (!isAdmin && from < today) {
      toast({ title: "Leave cannot start in the past. Pick today or a future date.", status: "warning" });
      return;
    }
    setBusy(true);
    try {
      await api("/leaves", { method: "POST", body: JSON.stringify({ leaveType: type, fromDate: from, toDate: to, reason }) });
      toast({ title: "Leave request submitted.", status: "success" });
      // Reset
      setType("annual"); setFrom(""); setTo(""); setReason("");
      onSubmitted && onSubmitted();
    } catch (e) {
      toast({ title: e.data?.message || e.message || "Could not submit leave.", status: "error" });
    } finally { setBusy(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <Modal>
        <Header title="Apply for Leave" onClose={onClose} />
        <Body>
          <Field label="LEAVE TYPE">
            <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
              {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="FROM DATE *">
              <input type="date" value={from} min={isAdmin ? undefined : today} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="TO DATE *">
              <input type="date" value={to} min={isAdmin ? undefined : (from || today)} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <Field label="REASON">
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Brief reason for your leave..."
              style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
            />
          </Field>
        </Body>
        <Footer>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={btnPrimary} disabled={busy}>{busy ? "Submitting…" : "Submit Request"}</button>
        </Footer>
      </Modal>
    </Backdrop>
  );
}

// ─── Shared modal primitives ─────────────────────────────────────────────
export function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "modalFadeIn 160ms ease",
      }}
    >
      {children}
      <style>{`@keyframes modalFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
export function Modal({ children, maxWidth = 480 }) {
  return (
    <div style={{
      width: "100%", maxWidth, maxHeight: "92vh", overflow: "auto",
      background: "var(--bg-surface)", borderRadius: 14, boxShadow: "var(--shadow-lg)",
      border: "1px solid var(--border-subtle)",
    }}>{children}</div>
  );
}
export function Header({ title, onClose }) {
  return (
    <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{title}</h3>
      <button onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", color: "var(--text-tertiary)", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}
export function Body({ children }) {
  return <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>;
}
export function Footer({ children }) {
  return <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 10 }}>{children}</div>;
}
// Field now lives in Components/ui/Field. Re-exported here because PayrollRunPage,
// WfhPage, RegularisationPage and RegularisationAdminPage already pull it from this
// module alongside Backdrop/Modal/Header/Body/Footer/inputStyle — keeping the
// re-export means one definition without rewriting four import lines.
export { Field };
export const inputStyle = {
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 13,
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
  width: "100%",
  fontFamily: "inherit",
};
export const btnPrimary = {
  background: "var(--accent-primary)", color: "white", border: "none",
  padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
};
export const btnGhost = {
  background: "transparent", color: "var(--text-secondary)",
  border: "none", padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
};
